import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Truck, Printer, Tag } from "lucide-react";
import api, { fmtJMD } from "../../lib/api";

export default function AdminManifest() {
  const [day, setDay] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  });
  const [data, setData] = useState(null);

  const load = async () => {
    setData(null);
    try {
      const r = await api.get(`/admin/manifest?day=${day}`);
      setData(r.data);
    } catch { setData({ groups: {}, totals: { count: 0, revenue: 0, pops: 0 } }); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [day]);

  const parishes = data ? Object.keys(data.groups).sort() : [];
  const totalOrders = data?.totals?.count || 0;

  return (
    <div data-testid="admin-manifest">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3 print:hidden">
        <div>
          <div className="overline text-zinc-500">Operations</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Delivery manifest</h1>
          <p className="text-sm text-zinc-500 mt-1">All orders scheduled for a delivery day, grouped by parish.</p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <input type="date" value={day} onChange={(e) => setDay(e.target.value)}
                 className="border border-zinc-200 rounded-lg px-3 py-2 text-sm"
                 data-testid="manifest-day" />
          <button onClick={() => window.print()}
                  className="btn-primary inline-flex items-center gap-2"
                  data-testid="print-manifest-btn">
            <Printer size={14} /> Print manifest
          </button>
          <Link to={`/admin/manifest/labels?day=${day}`}
                className="btn-ghost inline-flex items-center gap-2"
                data-testid="print-labels-btn">
            <Tag size={14} /> Print all labels
          </Link>
        </div>
      </div>

      {!data ? <div className="text-sm text-zinc-500">Loading…</div> : (
        <>
          <div className="grid grid-cols-3 gap-3 mb-6">
            <Stat label="Orders" value={totalOrders} />
            <Stat label="Total pops" value={data.totals.pops} />
            <Stat label="Revenue" value={fmtJMD(data.totals.revenue)} />
          </div>

          {totalOrders === 0 ? (
            <div className="rounded-xl border border-dashed border-zinc-200 p-10 text-center">
              <Truck size={28} className="mx-auto text-zinc-300 mb-3" />
              <p className="text-sm text-zinc-500">No orders scheduled for this day.</p>
            </div>
          ) : (
            parishes.map((p) => (
              <section key={p} className="mb-8 rounded-2xl border border-zinc-200 bg-white overflow-hidden print:break-inside-avoid">
                <div className="px-5 py-3 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
                  <div className="font-display font-bold">{p}</div>
                  <div className="text-xs text-zinc-500">{data.groups[p].length} orders</div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[700px]">
                    <thead className="text-xs text-zinc-500">
                      <tr>
                        <th className="text-left px-4 py-2">#</th>
                        <th className="text-left px-4 py-2">Customer</th>
                        <th className="text-left px-4 py-2">Phone</th>
                        <th className="text-left px-4 py-2">Address</th>
                        <th className="text-left px-4 py-2">Items</th>
                        <th className="text-left px-4 py-2">Notes</th>
                        <th className="text-right px-4 py-2">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.groups[p].map((o, i) => (
                        <tr key={o.id} className="border-t border-zinc-100" data-testid={`manifest-order-${o.id}`}>
                          <td className="px-4 py-3 font-mono text-xs">{i + 1}</td>
                          <td className="px-4 py-3 font-semibold whitespace-nowrap">{o.customer_name}</td>
                          <td className="px-4 py-3 text-xs">{o.customer_phone}</td>
                          <td className="px-4 py-3 text-xs break-words">{o.address_line}</td>
                          <td className="px-4 py-3 text-xs">
                            {o.items.map((it, idx) => (
                              <div key={idx}>
                                {it.quantity}× {it.name}
                              </div>
                            ))}
                          </td>
                          <td className="px-4 py-3 text-xs text-zinc-500 max-w-[200px] break-words">
                            {o.delivery_notes || o.notes || ""}
                          </td>
                          <td className="px-4 py-3 text-right font-semibold whitespace-nowrap">{fmtJMD(o.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            ))
          )}
        </>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-4 sm:p-5">
      <div className="overline text-zinc-500 truncate">{label}</div>
      <div className="font-display text-xl sm:text-2xl font-bold mt-2">{value}</div>
    </div>
  );
}
